import { useState, useEffect } from 'react';
import { bffQuery } from '../lib/bffClient';

/**
 * What the clinic has already recorded for one patient, by bank-transfer reference —
 * the payments of their invoices and the rows of their appointment deposits (PM-0711).
 * One round trip, asked for only once a bank transfer is being entered (`enabled`) and
 * kept for the patient after that.
 *
 * The answer feeds a WARNING and nothing else, so it is best-effort by design: a BFF
 * without the field, a caller without the right, or a network error leaves both lists
 * empty and the form exactly as it was. It never blocks and never shows a figure.
 */
const PATIENT_REFERENCES = `
  query PatientReferences($patientId: ID!) {
    patientAccount(patientId: $patientId) {
      payments { id amount paymentMethod referenceNumber status kind createdAt currency }
    }
    patientAppointmentDeposits(patientId: $patientId) {
      deposit { appointmentId rows { id amountRial source paymentMethod note createdAt currency } }
    }
  }
`;

const NONE = { payments: [], entries: [] };

export function usePatientReferences(patientId, enabled = true) {
  const [known, setKnown] = useState({ patientId: null, ...NONE });

  useEffect(() => {
    if (!enabled || !patientId || known.patientId === patientId) return undefined;
    let stale = false;
    (async () => {
      try {
        const data = await bffQuery(PATIENT_REFERENCES, { patientId }, 'PatientReferences');
        if (stale) return;
        setKnown({
          patientId,
          payments: data?.patientAccount?.payments || [],
          entries: data?.patientAppointmentDeposits || [],
        });
      } catch {
        if (stale) return;
        // Remember the failure for this patient: asking again on every keystroke would not help.
        setKnown({ patientId, ...NONE });
      }
    })();
    return () => { stale = true; };
  }, [patientId, enabled, known.patientId]);

  return known.patientId === patientId ? known : { patientId, ...NONE };
}
